import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  FolderKanban,
  History,
  MessageSquareText,
  Pencil,
  Plus,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { PeoplePicker } from '@/components/projects/PeoplePicker';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { useTasks } from '@/features/projects/hooks';
import {
  useAddEngMembers,
  useCreateEngNote,
  useDeleteEngNote,
  useEngCosts,
  useEngFiles,
  useEngNotes,
  useEngPrints,
  useEngStatusHistory,
  useRemoveEngMember,
  useSetEngMemberRole,
  type EngProject,
} from '@/features/engineering/hooks';
import {
  ENG_MEMBER_ROLE_LABELS,
  ENG_MEMBER_ROLE_OPTIONS,
  getEngStatusMeta,
  type EngMemberRole,
} from '@/features/engineering/model';
import { formatError } from '@/lib/errors';
import { formatCurrency, formatDateOnly, formatDateTime, formatRelative, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { BudgetBar, EngStatusBadge, KV, PersonChip } from './EngParts';
import { EngItemLinksCard } from './EngItemLinksCard';
import { EditEngProjectDialog } from './EditEngProjectDialog';

interface EngOverviewTabProps {
  project: EngProject;
  canEdit: boolean;
  canManage: boolean;
  onGoToTab: (tab: string) => void;
}

export function EngOverviewTab({ project, canEdit, canManage, onGoToTab }: EngOverviewTabProps) {
  const [editOpen, setEditOpen] = useState(false);
  const { data: files } = useEngFiles(project.id);
  const { data: prints } = useEngPrints(project.id);
  const { data: costs } = useEngCosts(project.id);
  const { data: tasks } = useTasks(project.pm_project_id ?? undefined);

  const today = todayIsoDate();
  const spent = useMemo(() => {
    const logged = (costs ?? []).reduce((s, c) => s + c.amount_cents, 0);
    const printSpend = (prints ?? []).reduce((s, p) => s + (p.cost_cents ?? 0), 0);
    return logged + printSpend;
  }, [costs, prints]);
  const openTasks = (tasks ?? []).filter((t) => !t.is_completed && !t.parent_task_id).length;
  const totalTasks = (tasks ?? []).filter((t) => !t.parent_task_id).length;
  const late = project.target_date && project.target_date < today && !getEngStatusMeta(project.status).locked;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
            <div className="space-y-1">
              <CardTitle>Project details</CardTitle>
              <CardDescription>
                {project.description ? project.description : 'No description yet.'}
              </CardDescription>
            </div>
            {canEdit && (
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid gap-x-8 sm:grid-cols-2">
              <div className="divide-y divide-border">
                <KV label="Status" value={<EngStatusBadge status={project.status} />} />
                <KV label="Owner" value={<PersonChip person={project.owner} size="xs" />} />
                <KV
                  label="Start date"
                  value={
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDateOnly(project.start_date)}
                    </span>
                  }
                />
                <KV
                  label="Target date"
                  value={
                    <span className={cn('inline-flex items-center gap-1.5', late && 'font-medium text-fresh-salmon-dark')}>
                      <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatDateOnly(project.target_date)}
                      {late && <Badge variant="warning-soft">Past due</Badge>}
                    </span>
                  }
                />
              </div>
              <div className="divide-y divide-border">
                <KV
                  label="Budget"
                  value={
                    <div className="min-w-[160px]">
                      <div>
                        <span className="tabular-nums">{formatCurrency(spent)}</span>
                        <span className="text-muted-foreground">
                          {project.budget_cents != null ? ` of ${formatCurrency(project.budget_cents)}` : ' spent · no budget set'}
                        </span>
                      </div>
                      <BudgetBar spentCents={spent} budgetCents={project.budget_cents} className="mt-1.5" />
                    </div>
                  }
                />
                <KV
                  label="Files"
                  value={
                    <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => onGoToTab('files')}>
                      {(files ?? []).length} file{(files ?? []).length === 1 ? '' : 's'}
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  }
                />
                <KV
                  label="3D prints"
                  value={
                    <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => onGoToTab('prints')}>
                      {(prints ?? []).length} logged
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  }
                />
                <KV
                  label="Tasks"
                  value={
                    project.pm_project_id ? (
                      <button type="button" className="inline-flex items-center gap-1 hover:underline" onClick={() => onGoToTab('tasks')}>
                        {openTasks} open of {totalTasks}
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    ) : (
                      <span className="text-muted-foreground">Not linked</span>
                    )
                  }
                />
              </div>
            </div>
            {project.design_locked_at && (
              <p className="mt-4 rounded-lg border border-bright-moss/50 bg-bright-moss/15 px-3 py-2 text-xs text-bright-moss-dark">
                Design locked {formatDateTime(project.design_locked_at)}. Files are read-only until the project is reopened.
              </p>
            )}
          </CardContent>
        </Card>

        <NotesCard projectId={project.id} canEdit={canEdit} />
      </div>

      <div className="space-y-6">
        <TeamCard project={project} canManage={canManage} />
        <EngItemLinksCard projectId={project.id} canEdit={canEdit} />
        <PmProjectCard project={project} onGoToTab={onGoToTab} />
        <HistoryCard projectId={project.id} />
      </div>

      <EditEngProjectDialog project={project} open={editOpen} onOpenChange={setEditOpen} canManage={canManage} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------

function TeamCard({ project, canManage }: { project: EngProject; canManage: boolean }) {
  const { data: profiles } = useProfiles();
  const addMembers = useAddEngMembers();
  const removeMember = useRemoveEngMember();
  const setRole = useSetEngMemberRole();
  const [addOpen, setAddOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [pickedRole, setPickedRole] = useState<EngMemberRole>('engineer');

  const members = useMemo(
    () =>
      [...project.eng_project_members].sort((a, b) => {
        if (a.profile_id === project.owner_id) return -1;
        if (b.profile_id === project.owner_id) return 1;
        return (a.profiles?.full_name ?? a.profiles?.email ?? '').localeCompare(
          b.profiles?.full_name ?? b.profiles?.email ?? '',
        );
      }),
    [project.eng_project_members, project.owner_id],
  );
  const memberIds = new Set(members.map((m) => m.profile_id));
  const candidates = (profiles ?? [])
    .filter((p) => p.is_active !== false && !memberIds.has(p.id))
    .map((p) => ({ id: p.id, full_name: p.full_name, email: p.email, avatar_url: p.avatar_url }));

  const submitAdd = async () => {
    try {
      await addMembers.mutateAsync({ projectId: project.id, profileIds: picked, role: pickedRole });
      toast.success(picked.length === 1 ? 'Team member added.' : `${picked.length} people added.`);
      setAddOpen(false);
      setPicked([]);
    } catch (err) {
      toast.error(formatError(err, 'Could not add people.'));
    }
  };

  const handleRemove = async (profileId: string, label: string) => {
    if (!window.confirm(`Remove ${label} from the team?`)) return;
    try {
      await removeMember.mutateAsync({ projectId: project.id, profileId });
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div className="space-y-1">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4 text-info" />
            Team
          </CardTitle>
          <CardDescription>The team can upload files, log prints and costs.</CardDescription>
        </div>
        {canManage && (
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
            <UserPlus className="h-3.5 w-3.5" />
            Add
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody on the team yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {members.map((m) => {
              const isOwner = m.profile_id === project.owner_id;
              const label = m.profiles?.full_name ?? m.profiles?.email ?? 'Member';
              return (
                <li key={m.profile_id} className="flex items-center gap-2 py-2">
                  <PersonChip person={m.profiles} className="min-w-0 flex-1" subtitle={isOwner ? 'Owner' : undefined} />
                  {canManage && !isOwner ? (
                    <Select
                      value={m.role}
                      onValueChange={(v) =>
                        setRole.mutate(
                          { projectId: project.id, profileId: m.profile_id, role: v as EngMemberRole },
                          { onError: (err) => toast.error(formatError(err)) },
                        )
                      }
                    >
                      <SelectTrigger className="h-8 w-[118px] text-xs" aria-label={`Role for ${label}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENG_MEMBER_ROLE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-xs">
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="text-[11px]">
                      {ENG_MEMBER_ROLE_LABELS[m.role] ?? m.role}
                    </Badge>
                  )}
                  {canManage && !isOwner && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => void handleRemove(m.profile_id, label)}
                      aria-label={`Remove ${label}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) setPicked([]); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to the team</DialogTitle>
            <DialogDescription>They’ll be able to edit this project. Everyone else can still view it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <PeoplePicker profiles={candidates} value={picked} onChange={setPicked} placeholder="Pick people…" />
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Role</p>
              <Select value={pickedRole} onValueChange={(v) => setPickedRole(v as EngMemberRole)}>
                <SelectTrigger aria-label="Role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENG_MEMBER_ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} disabled={addMembers.isPending}>
              Cancel
            </Button>
            <Button onClick={() => void submitAdd()} disabled={addMembers.isPending || picked.length === 0}>
              {addMembers.isPending ? 'Adding…' : `Add ${picked.length || ''}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

function NotesCard({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const { profile, role } = useAuth();
  const { data: notes, isLoading } = useEngNotes(projectId);
  const create = useCreateEngNote();
  const remove = useDeleteEngNote();
  const [body, setBody] = useState('');

  const submit = async () => {
    if (!body.trim()) return;
    try {
      await create.mutateAsync({ projectId, body });
      setBody('');
    } catch (err) {
      toast.error(formatError(err, 'Could not add the note.'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquareText className="h-4 w-4 text-info" />
          Engineering log
        </CardTitle>
        <CardDescription>Decisions, test results, things to remember. Newest first.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {canEdit && (
          <div className="space-y-2">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What happened? What did we decide?"
              className="min-h-[72px]"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void submit();
              }}
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={() => void submit()} disabled={create.isPending || !body.trim()}>
                <Plus className="h-3.5 w-3.5" />
                Add note
              </Button>
            </div>
          </div>
        )}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (notes ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes yet.</p>
        ) : (
          <ul className="space-y-3">
            {(notes ?? []).map((note) => {
              const mine = note.author_id === profile?.id;
              const canDelete = mine || role === 'super_admin' || role === 'admin';
              return (
                <li key={note.id} className="rounded-lg border border-border bg-card px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <PersonChip person={note.author} size="xs" subtitle={formatRelative(note.created_at)} />
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        aria-label="Delete note"
                        onClick={() => {
                          if (!window.confirm('Delete this note?')) return;
                          remove.mutate({ id: note.id, projectId }, { onError: (err) => toast.error(formatError(err)) });
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-foreground">{note.body}</p>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PM project + history
// ---------------------------------------------------------------------------

function PmProjectCard({ project, onGoToTab }: { project: EngProject; onGoToTab: (tab: string) => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FolderKanban className="h-4 w-4 text-info" />
          Task list
        </CardTitle>
      </CardHeader>
      <CardContent>
        {project.pm_project ? (
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Link to={`/projects/${project.pm_project.id}`} className="block truncate text-sm font-medium hover:underline">
                {project.pm_project.name}
              </Link>
              <p className="text-xs text-muted-foreground">
                Tasks section{project.pm_project.archived_at ? ' · archived' : ''}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => onGoToTab('tasks')}>
              Tasks
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : project.pm_project_id ? (
          <p className="text-sm text-muted-foreground">
            Linked to a task list you aren’t a member of. Ask the task list owner to share it with you.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not linked to a task list yet. Use the Tasks tab to link or create one.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function HistoryCard({ projectId }: { projectId: string }) {
  const { data: history, isLoading } = useEngStatusHistory(projectId);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-4 w-4 text-info" />
          Status history
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (history ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No changes recorded.</p>
        ) : (
          <ol className="relative space-y-3 border-l border-border pl-4">
            {(history ?? []).map((row) => {
              const meta = getEngStatusMeta(row.to_status);
              return (
                <li key={row.id} className="relative">
                  <span
                    aria-hidden
                    className={cn(
                      'absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card',
                      meta.locked ? 'bg-bright-moss-dark' : 'bg-info',
                    )}
                  />
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    {row.from_status && (
                      <>
                        <span className="text-muted-foreground">{getEngStatusMeta(row.from_status).shortLabel}</span>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                      </>
                    )}
                    <EngStatusBadge status={row.to_status} />
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {formatDateTime(row.created_at)}
                    {row.actor && ` · ${row.actor.full_name ?? row.actor.email}`}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
